import type {
  CheqSettings,
  PartnerName,
  PartnerParams,
  PartnerPixelConfig,
} from "@ascent/papi-core";
import { partnerModulesConfig } from "./partners";
import type { PartnerHandlerContext } from "./types/partner";

// Shared in-memory registry so configs can be supplied after bundle load
let currentPartnerPixels: PartnerPixelConfig[] = [];

export function setPartnerPixels(configs: PartnerPixelConfig[]): void {
  currentPartnerPixels = Array.isArray(configs) ? configs.slice() : [];
}

export function appendPartnerPixels(configs: PartnerPixelConfig[]): void {
  if (!Array.isArray(configs) || configs.length === 0) return;
  currentPartnerPixels = currentPartnerPixels.concat(configs);
}

export function clearPartnerPixels(): void {
  currentPartnerPixels = [];
}

function validateAndFirePartner(
  partnerName: PartnerName,
  params: PartnerParams,
  ctx: PartnerHandlerContext,
) {
  console.log("validateAndFirePartner", partnerName, params, ctx);

  const config = partnerModulesConfig[partnerName];
  if (!config) {
    console.error(`Unsupported partner: ${partnerName}`);
    return;
  }

  config
    .loader()
    .then((module) => {
      const handler = module[config.handlerName];
      if (typeof handler !== "function") {
        console.error(
          `Handler function '${config.handlerName}' not found in module for partner: ${partnerName}`,
        );
        return;
      }
      try {
        const args = config.getHandlerArgs(params, ctx);
        // eslint-disable-next-line prefer-spread
        handler.apply(null, args);
      } catch (error: unknown) {
        console.error(`${partnerName} handler error`, error);
      }
    })
    .catch((error: unknown) => {
      console.error(`Failed to load module for partner: ${partnerName}`, error);
    });
}

/**
 * Initialize Cheq conversion tracking
 * @param uniqueVisitorId - The unique visitor ID (legacy used page_id, ascent version uses request_id)
 * @param campaignId - The campaign ID
 * @param settings - The Cheq settings
 */
export function initCheq(
  uniqueVisitorId: string,
  campaignId: string,
  settings?: CheqSettings,
) {
  const {
    disable_cheq = false,
    // event name that Cheq fires for negative audience targeting
    cheq_neg_audience_event = "cq_98e2zj4j",
    allow_negative_audience = false,
    class_name = "ct_clicktrue_28382",
    //data channel
    ch = "cheq4ppc",
    script_url = "https://ob.system1onesource.com/i/35289458b2de2bf5220f730bdbc66486.js",
    partner_pixels = [],
  } = settings ?? {};

  if (disable_cheq) {
    return;
  }
  // Load the cheq script on the page
  const script = document.createElement("script");
  script.src = script_url;
  script.async = true;
  script.className = class_name;
  script.dataset.ch = ch;
  script.dataset.uvid = uniqueVisitorId;
  script.dataset.utmCampaign = campaignId;
  document.head.appendChild(script);

  // Initialize partner pixels registry if provided at init time
  if (Array.isArray(partner_pixels) && partner_pixels.length > 0) {
    setPartnerPixels(partner_pixels);
  }

  console.log("allow_negative_audience", allow_negative_audience);
  // if allow_negative_audience is enabled set up event listeners
  if (allow_negative_audience) {
    const searchParams = new URLSearchParams(window.location.search);
    const utmSource = searchParams.get("utm_source")?.toLowerCase() ?? "";

    window.addEventListener(cheq_neg_audience_event, () => {
      // console.group("Cheq");
      // console.log("cheq_neg_audience_event", cheq_neg_audience_event);
      // console.log("currentPartnerPixels", currentPartnerPixels);
      const configs = currentPartnerPixels;
      if (!Array.isArray(configs) || configs.length === 0) {
        return;
      }
      for (const config of configs) {
        if (config?.enabled === false) {
          continue;
        }
        const prefixes = (config?.utm_source_prefixes ?? []).map((p) =>
          p.toLowerCase(),
        );
        const matches = prefixes.some((p) => utmSource.startsWith(p));
        if (!matches) {
          continue;
        }
        // Prefer partnerName/params; fallback to legacy handler for flexibility
        if (config.partner_name && config.params) {
          validateAndFirePartner(config.partner_name, config.params, {
            pageId: uniqueVisitorId,
            campaignId,
            utmSource,
            cheqEventName: cheq_neg_audience_event,
            window,
          });
        }
      }
      console.groupEnd();
    });
  }
}
